type Day =
  | "monday"
  | "tuesday"
  | "wednesday"
  | "thursday"
  | "friday"
  | "saturday"
  | "sunday";

export type OpeningHour = {
  startDay: Day;
  closeDay: Day;
  startHour: string;
  closeHour: string;
};

const DAYS_ORDER: Day[] = [
  "sunday", // JS Date getDay() starts with Sunday = 0
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

const MINUTES_IN_DAY = 1440; // 24 hours * 60 minutes

// Convert HH:MM string to minutes since midnight
const toMinutes = (time: string): number => {
  const [h, m] = time.split(":").map(Number);
  return h * 60 + m;
};

// Helper: Get current day name
function getTodayName(date = new Date()): Day {
  return DAYS_ORDER[date.getDay()];
}

// Build a full week map of time ranges per day
function buildWeekMap(
  openings: OpeningHour[]
): Record<
  Day,
  { start: number; end: number; originalEnd?: number; originalCloseDay?: Day }[]
> {
  const week: Record<
    Day,
    {
      start: number;
      end: number;
      originalEnd?: number;
      originalCloseDay?: Day;
    }[]
  > = {
    sunday: [],
    monday: [],
    tuesday: [],
    wednesday: [],
    thursday: [],
    friday: [],
    saturday: [],
  };

  for (const { startDay, closeDay, startHour, closeHour } of openings) {
    const startIdx = DAYS_ORDER.indexOf(startDay);
    const endIdx = DAYS_ORDER.indexOf(closeDay);
    const start = toMinutes(startHour);
    const end = toMinutes(closeHour);

    if (startDay === closeDay) {
      // Same day operation
      week[startDay].push({ start, end });
    } else {
      // Cross-day operation
      // First day: from start time to end of day, but store original closing info
      week[startDay].push({
        start,
        end: MINUTES_IN_DAY,
        originalEnd: end,
        originalCloseDay: closeDay,
      });

      // Last day: from start of day (00:00 = 0 minutes) to end time
      week[closeDay].push({ start: 0, end });

      // Any days in between are fully open (00:00 to 24:00)
      let currentIdx = (startIdx + 1) % 7;
      while (currentIdx !== endIdx) {
        const day = DAYS_ORDER[currentIdx];
        week[day].push({ start: 0, end: MINUTES_IN_DAY });
        currentIdx = (currentIdx + 1) % 7;
      }
    }
  }

  // Sort time ranges for each day
  for (const day of DAYS_ORDER) {
    week[day].sort((a, b) => a.start - b.start);
  }

  return week;
}

// Main status function
export function getLabelForHours(
  hours: OpeningHour[],
  now: Date = new Date()
): string[] {
  const weekMap = buildWeekMap(hours);
  const today = getTodayName(now);
  const currentMinutes = now.getHours() * 60 + now.getMinutes();
  const todayRanges = weekMap[today];

  for (const range of todayRanges) {
    if (currentMinutes >= range.start && currentMinutes < range.end) {
      // For cross-day ranges, show the original closing time and day
      if (range.originalEnd && range.originalCloseDay) {
        const closeTime = formatTime(range.originalEnd);
        const isClosingTomorrow =
          range.originalCloseDay ===
          DAYS_ORDER[(DAYS_ORDER.indexOf(today) + 1) % 7];
        const closingText = isClosingTomorrow
          ? `taking orders until ${closeTime}`
          : `taking orders until ${closeTime} on ${capitalize(
              range.originalCloseDay
            )}`;
        return ["Open now", closingText];
      }
      return ["Open now", `taking orders until ${formatTime(range.end)}`];
    }
    if (currentMinutes < range.start) {
      return [
        `Not taking orders`,
        `Taking orders again at ${formatTime(range.start)}`,
      ];
    }
  }

  // If today is entirely closed, find next open day
  for (let i = 1; i < 7; i++) {
    const nextDay = DAYS_ORDER[(DAYS_ORDER.indexOf(today) + i) % 7];
    const nextRanges = weekMap[nextDay];
    if (nextRanges.length > 0) {
      const nextTime = formatTime(nextRanges[0].start);
      return [
        `Not taking orders`,
        `Taking orders again ${
          i === 1 ? "tomorrow" : `on ${capitalize(nextDay)}`
        } at ${nextTime}`,
      ];
    }
  }

  return ["Not taking orders"];
}

// Format minutes since midnight into HH:MM (handle 24:00 as midnight)
function formatTime(minutes: number): string {
  const actualMinutes = minutes === MINUTES_IN_DAY ? 0 : minutes;
  const h = Math.floor(actualMinutes / 60)
    .toString()
    .padStart(2, "0");
  const m = (actualMinutes % 60).toString().padStart(2, "0");
  return `${h}:${m}`;
}

function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}
